import { css } from 'styled-components';
import { Iterable, pipe, Record } from '@blade/std-lib';
import { DELUGE_DARK_THEME } from './theme-palette';
export const createPaletteFromRadixPalette = (radixSwatch) => {
    const entries = Object.entries(radixSwatch);
    const baseName = /([A-Za-z]+)\d.*/.exec(entries[0][0])[1];
    const result = {};
    for (const [key, value] of entries) {
        const index = key.replace(baseName, '');
        result[index] = value;
    }
    return result;
};
export const createDelugeTheme = (opts) => {
    return {
        ...DELUGE_DARK_THEME,
        ...opts,
    };
};
const hexToRgb = (hex) => {
    const result = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    if (!result) {
        return undefined;
    }
    const [_, r, g, b] = result;
    return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
};
/** WCAG relative luminance of a `#rrggbb` colour, or nothing for others. */
const relativeLuminance = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) {
        return undefined;
    }
    const [r, g, b] = rgb.split(',').map((part) => {
        const channel = Number(part) / 255;
        return channel <= 0.039_28
            ? channel / 12.92
            : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const TEXT_ON_DARK = '#FFFFFF';
const TEXT_ON_LIGHT = '#111111';
/**
 * Dark text wins only by this much. On a mid-tone — the purple of the
 * Monokai theme — the two are within a few percent of each other by WCAG,
 * and white is what every other theme was designed with.
 */
const DARK_TEXT_MARGIN = 1.5;
/**
 * The text colour that reads on a solid fill of `hex`.
 *
 * White, unless dark text is clearly more readable — which is what a light
 * accent, a yellow, needs. Fixed white text was fine while every accent was
 * blue, and unreadable the moment the brand became yellow.
 */
export const contrastTextOn = (hex) => {
    const fill = relativeLuminance(hex);
    if (fill === undefined) {
        return TEXT_ON_DARK;
    }
    const ratio = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    const withWhite = ratio(fill, relativeLuminance(TEXT_ON_DARK));
    const withDark = ratio(fill, relativeLuminance(TEXT_ON_LIGHT));
    return withDark > withWhite * DARK_TEXT_MARGIN ? TEXT_ON_LIGHT : TEXT_ON_DARK;
};
export const createColorVariables = (theme, paletteVariant, alias = paletteVariant) => {
    const scale = theme.palette[paletteVariant];
    const solid = scale[9];
    const variables = pipe(Record.entries(scale), Iterable.flatMap(([step, value]) => {
        const rgb = hexToRgb(value);
        return [
            `--${alias}-${step}: ${value};`,
            rgb ? `--${alias}-${step}-rgb: ${rgb};` : undefined,
        ];
    }), Iterable.filter((it) => it !== undefined), Iterable.join('\n'));
    // Text that sits on the solid step: `color: var(--primary-contrast)` on a
    // `background: var(--primary-9)`. Only for opaque scales; an alpha scale
    // has no single colour for text to sit on.
    return relativeLuminance(solid) === undefined
        ? variables
        : `${variables}\n--${alias}-contrast: ${contrastTextOn(solid)};`;
};
const appearanceColor_ = {
    dark: '#000000',
    light: '#FFFFFF',
};
const invertedAppearanceColor_ = {
    dark: '#FFFFFF',
    light: '#000000',
};
// AA part for #rrggbbaa hex color with step 10 percent
const HexAA = ['00', '1A', '33', '4D', '66', '80', '99', 'B3', 'CC', 'E6', 'FF'];
export const createAppearanceVariablesForAppearanceColor = (theme) => {
    const appearanceColor = appearanceColor_[theme.appearance];
    const invertedAppearanceColor = invertedAppearanceColor_[theme.appearance];
    const baseColor = theme.appearanceColor;
    const rgb = hexToRgb(appearanceColor);
    const invertedRgb = hexToRgb(invertedAppearanceColor);
    return pipe([
        `--base-1: ${baseColor};`,
        rgb ? `--base-1-rgb: ${rgb};` : undefined,
        ...HexAA.flatMap((shade, i) => {
            const color = `${baseColor}${shade}`;
            const rgb = hexToRgb(color);
            return [
                `--baseA-${i}: ${color};`,
                rgb ? `--baseA-${i}-rgb: ${rgb};` : undefined,
            ];
        }),
        `--appearance-1: ${appearanceColor};`,
        rgb ? `--appearance-1-rgb: ${rgb};` : undefined,
        ...HexAA.flatMap((shade, i) => {
            const color = `${appearanceColor}${shade}`;
            const rgb = hexToRgb(color);
            return [
                `--appearanceA-${i}: ${color};`,
                rgb ? `--appearanceA-${i}-rgb: ${rgb};` : undefined,
            ];
        }),
        `--appearance-inv-1: ${invertedAppearanceColor};`,
        invertedRgb ? `--appearance-inv-1-rgb: ${invertedRgb};` : undefined,
        ...HexAA.flatMap((shade, i) => {
            const color = `${invertedAppearanceColor}${shade}`;
            const rgb = hexToRgb(color);
            return [
                `--appearanceA-inv-${i}: ${color};`,
                rgb ? `--appearanceA-inv-${i}-rgb: ${rgb};` : undefined,
            ];
        }),
    ], Iterable.filter((it) => it !== undefined), Iterable.join('\n'));
};
export const createThemeVariablesCss = (theme) => css `
  ${createAppearanceVariablesForAppearanceColor(theme)}
  ${[...Record.keys(theme.palette)]
    .map((it) => createColorVariables(theme, it))
    .join('\n')}
`;
//# sourceMappingURL=theme-create.js.map